import { useState, useEffect, useCallback, useRef } from 'react';

const POLL_INTERVAL = 30000; // 30 seconds
// Use backend proxy to avoid CORS issues
const HEALTH_ENDPOINT = '/v1/tts/infer-health/';

export function useInferHealth() {
  const [healthStatus, setHealthStatus] = useState('unknown');
  const [healthDetails, setHealthDetails] = useState(null);
  const [lastChecked, setLastChecked] = useState(null);
  const intervalRef = useRef(null);

  const checkHealth = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      const response = await fetch(HEALTH_ENDPOINT, {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
        },
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        setHealthDetails(data);
        
        // Determine health status based on various factors
        if (data.status === 'healthy' || data.status === 'ok') {
          // Additional checks for degraded state
          if (data.metrics) {
            const { cpu_percent, memory_percent, gpu_memory_percent, error_rate, active_requests } = data.metrics;
            
            // Special handling for pooled workers - high GPU memory with pooled models is expected
            // With 2 models x 8 pooled workers each, high GPU memory (90-96%) is normal
            const isHighGPUNormal = gpu_memory_percent > 90 && gpu_memory_percent <= 96 && error_rate === 0;
            
            // Consider degraded if resources are high or there are errors
            if (cpu_percent > 80 || memory_percent > 80 || gpu_memory_percent > 96 || error_rate > 0.05) {
              setHealthStatus('degraded');
            } else if (isHighGPUNormal) {
              // High GPU memory (90-95%) with no errors is normal for pooled models
              setHealthStatus('healthy');
              data.pooled_workers_detected = true;
              setHealthDetails(data);
            } else {
              setHealthStatus('healthy');
            }
          } else {
            setHealthStatus('healthy');
          }
        } else if (data.status === 'degraded' || data.status === 'critical') {
          // Check if this is just due to pooled workers with high GPU memory
          if (data.metrics) {
            const { gpu_memory_percent, error_rate } = data.metrics;
            // With pooled models, GPU memory 90-96% is normal operation
            if (gpu_memory_percent > 90 && gpu_memory_percent <= 96 && error_rate === 0) {
              setHealthStatus('healthy');
              data.pooled_workers_detected = true;
              setHealthDetails(data);
            } else {
              setHealthStatus('degraded');
            }
          } else {
            setHealthStatus('degraded');
          }
        } else {
          setHealthStatus('unhealthy');
        }
      } else {
        // Non-200 response
        setHealthStatus('unhealthy');
        setHealthDetails(null);
      }
      
      setLastChecked(new Date());
    } catch (error) {
      console.error('Health check failed:', error);
      setHealthStatus('unhealthy');
      setHealthDetails(null);
      setLastChecked(new Date());
    }
  }, []);

  useEffect(() => {
    // Initial check
    checkHealth();

    // Set up interval for periodic checks
    intervalRef.current = setInterval(checkHealth, POLL_INTERVAL);

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [checkHealth]);

  // Allow manual refresh
  const refresh = useCallback(() => {
    checkHealth();
  }, [checkHealth]);

  return {
    status: healthStatus,
    details: healthDetails,
    lastChecked,
    refresh,
  };
}